import { paddStart } from '@/utils';
import { Message, MessageEvent, OrderCardMessage } from '../types/message';
import { OrderItem } from '../types/order';

export const observeElementInnerHeight = (
  element: Element,
  callback: () => void,
  historyMessageCallback: () => void
) => {
  const observer = new MutationObserver(mutationsList => {
    mutationsList.forEach(mutation => {
      const { addedNodes, type } = mutation;
      if (type === 'childList') {
        if (addedNodes.length <= 0) {
          return;
        }
        const item = addedNodes[0];
        if ((item as HTMLElement)?.classList?.contains('csc-im-message-updated')) {
          return;
        }
        if ((item as HTMLElement)?.classList?.contains('csc-im-history-message')) {
          historyMessageCallback();
          return;
        }
        callback();
        if ((item as HTMLElement)?.classList?.contains('csc-im-message-stream')) {
          const streamContent = (item as HTMLElement)?.querySelector?.('.csc-im-message-text');
          if (streamContent) {
            const subObserver = observeElementInnerHeight(streamContent, callback, () => {});
            setTimeout(() => {
              subObserver.disconnect();
            }, 1000 * 60);
          }
          return;
        }
        if (!addedNodes?.forEach || typeof addedNodes.forEach !== 'function') {
          return;
        }
        addedNodes.forEach(node => {
          const imgages = (node as HTMLElement)?.querySelectorAll?.('img') || [];
          Array.from(imgages).forEach(img => {
            img.addEventListener('load', () => {
              setTimeout(() => {
                callback();
              }, 100);
            });
          });
          const videos = (node as HTMLElement)?.querySelectorAll?.('video') || [];
          Array.from(videos).forEach(video => {
            video.addEventListener('loadedmetadata', () => {
              setTimeout(() => {
                callback();
              }, 100);
            });
          });
        });
      }
    });
  });

  observer.observe(element, { childList: true });

  return { disconnect: () => observer.disconnect() };
};

export const scrollToElementBottom = (element: Element, parent: Element) => {
  const currentScrollTop = parent.scrollTop;
  const willScrollTop = element.clientHeight - parent.clientHeight;
  const step = (willScrollTop - currentScrollTop) / 5;
  let nextScrollTop = currentScrollTop;

  const scroll = () => {
    // eslint-disable-next-line no-param-reassign
    parent.scrollTop = Math.min(nextScrollTop, willScrollTop);
    if (nextScrollTop < willScrollTop) {
      nextScrollTop += step;
      requestAnimationFrame(scroll);
    }
  };

  requestAnimationFrame(scroll);
};

export const getFileStatus = (message: Partial<Message>) => {
  if (message.event === MessageEvent.MESSAGE) {
    if (
      message.data?.type === 'image' ||
      message.data?.type === 'voice' ||
      message.data?.type === 'video'
    ) {
      if (message.data.url) {
        return 'done';
      }
      return 'uploading';
    }
  }
  return false;
};

export const formatDateTime = (timestamp?: number, prevTimestamp?: number) => {
  if (!timestamp || (prevTimestamp && timestamp - prevTimestamp < 1000 * 60 * 3)) {
    return null;
  }

  const datetime = new Date(timestamp);
  const current = new Date();

  let date = '';
  if (datetime.getFullYear() !== current.getFullYear()) {
    date = `${datetime.getFullYear()}年 ${datetime.getMonth() + 1}月${datetime.getDate()}日 `;
  } else if (
    datetime.getMonth() !== current.getMonth() ||
    datetime.getDate() !== current.getDate()
  ) {
    date = `${datetime.getMonth() + 1}月${datetime.getDate()}日 `;
  }

  const time = `${paddStart(String(datetime.getHours()), 2, '0')}:${paddStart(
    String(datetime.getMinutes()),
    2,
    '0'
  )}`;

  return date ? `${date} ${time}` : time;
};

export default observeElementInnerHeight;

export const formatOrderCard = (
  content: OrderCardMessage['data']['content'][number]
): OrderItem => {
  return {
    order_display_id: content.orderId,
    order_id: content.orderId,
    order_type: content.orderType,
    order_time: content.orderTime,
    order_status: content.orderStatus,
    order_status_cn: content.orderStatusCn,
    addr_info: [{ name: content.orderAddress?.from }, { name: content.orderAddress?.to }]
  };
};

function escapeHTML(str: string): string {
  return str.replace(
    /[&<>"']/g,
    m =>
      ({
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#39;'
      }[m] || m)
  );
}

export function replaceSensitiveWords(input: string, keys: string[]): string {
  if (keys.length === 0) return input;

  const keywords = keys.sort((k1, k2) => k2.length - k1.length);
  const escapedInput = escapeHTML(input);
  const escapedKeywords = keywords.map(k => escapeHTML(k).replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const regex = new RegExp(`(${escapedKeywords.join('|')})`, 'gi');
  return escapedInput.replace(regex, (_, p1) => '*'.repeat(p1.length));
}

const rateText = ['非常不满意', '不满意', '一般', '满意', '非常满意'];

export const getRateText = (rate: number) => {
  if (rate < 1 || rate > 5) {
    return '';
  }
  return rateText[rate - 1];
};
